import React from "react";
import ReactMarkdown from "react-markdown";
import { Typography, Box, Stack } from "@mui/joy";

interface MarkdownRendererProps {
  content: string;
  variant?: "body-lg" | "body-md" | "body-sm";
  sx?: any;
  startDecorator?: React.ReactNode;
  baseColor?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  variant = "body-md",
  sx = {},
  startDecorator,
  baseColor = "#7B7F85"
}) => {
  // Convert \n escape sequences to actual newlines
  const processedContent = content.replace(/\\n/g, '\n');
  
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        ...sx
      }}
    >
      {startDecorator && (
        <Box component="span" sx={{width: "24px", height: "24px"}}>
          {startDecorator}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ReactMarkdown
          components={{
        // Override paragraph styling to match Typography
        p: ({ children }) => (
          <Typography
            level={variant}
            component="p"
            sx={{
              color: baseColor,
              lineHeight: 1.6,
              mb: 2,
              "&:last-child": { mb: 0 },
            }}
          >
            {children}
          </Typography>
        ),
        // Style headings
        h1: ({ children }) => (
          <Typography
            level="h1"
            sx={{
              fontSize: { xs: "1.75rem", md: "2rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 2,
              mt: 3,
            }}
          >
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography
            level="h2"
            sx={{
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 2,
              mt: 3,
            }}
          >
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography
            level="h3"
            sx={{
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 1.5,
              mt: 2.5,
            }}
          >
            {children}
          </Typography>
        ),
        // Style lists
        ul: ({ children }) => (
          <Typography
            level={variant}
            component="ul"
            sx={{
              color: baseColor,
              lineHeight: 1.6,
              mb: 2,
              pl: 3,
              "&:last-child": { mb: 0 },
            }}
          >
            {children}
          </Typography>
        ),
        ol: ({ children }) => (
          <Typography
            level={variant}
            component="ol"
            sx={{
              color: baseColor,
              lineHeight: 1.6,
              mb: 2,
              pl: 3,
              "&:last-child": { mb: 0 },
            }}
          >
            {children}
          </Typography>
        ),
        // Style list items
        li: ({ children }) => (
          <Typography
            level={variant}
            component="li"
            sx={{
              color: baseColor,
              lineHeight: 1.6,
              mb: 0.5,
            }}
          >
            {children}
          </Typography>
        ),
        // Style strong/bold text
        strong: ({ children }) => (
          <Typography
            level={variant}
            component="span"
            sx={{
              color: "#1A1B1D",
              fontWeight: 600,
            }}
          >
            {children}
          </Typography>
        ),
        // Style emphasis/italic text
        em: ({ children }) => (
          <Typography
            level={variant}
            component="span"
            sx={{
              color: baseColor,
              fontStyle: "italic",
            }}
          >
            {children}
          </Typography>
        ),
        // Style links
        a: ({ children, href }) => (
          <Typography
            level={variant}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "#2E2D7E",
              textDecoration: "underline",
              "&:hover": {
                color: "#1a1a5e",
              },
            }}
          >
            {children}
          </Typography>
        ),
        // Style code blocks
        code: ({ children, className }) => {
          const isInline = !className;
          return isInline ? (
            <Typography
              level={variant}
              component="code"
              sx={{
                backgroundColor: "#f5f5f5",
                color: "#1A1B1D",
                padding: "2px 4px",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontSize: "0.9em",
              }}
            >
              {children}
            </Typography>
          ) : (
            <Typography
              level={variant}
              component="pre"
              sx={{
                backgroundColor: "#f5f5f5",
                color: "#1A1B1D",
                padding: 2,
                borderRadius: "8px",
                fontFamily: "monospace",
                fontSize: "0.9em",
                overflow: "auto",
                mb: 2,
              }}
            >
              <code>{children}</code>
            </Typography>
          );
        },
        // Style blockquotes
        blockquote: ({ children }) => (
          <Typography
            level={variant}
            component="blockquote"
            sx={{
              color: baseColor,
              lineHeight: 1.6,
              mb: 2,
              pl: 2,
              borderLeft: "4px solid #2E2D7E",
              fontStyle: "italic",
            }}
          >
            {children}
          </Typography>
        ),
      }}
    >
      {processedContent}
    </ReactMarkdown>
      </Box>
    </Stack>
  );
};

export default MarkdownRenderer; 